/**
 * Welches Projekt die Schiene in ihrer Projektgruppe führt.
 *
 * Die Vorlage hat diese Frage nicht: Sie zeigt immer „Projekt CUB", also ein
 * bereits gewähltes Projekt, und kennt den Zustand „Alle Projekte" nicht. Die
 * Anwendung kennt ihn — und daran ist die Gruppe zweimal gescheitert:
 *
 * 1. Sie hing allein an der Auswahl. Deren Vorgabe ist „Alle Projekte", und
 *    seit die Reiterleiste entfallen ist, gab es keinen zweiten Weg — Übersicht,
 *    Puls, Meilensteine, Lieferung und Seiten waren dann **überhaupt nicht**
 *    erreichbar.
 * 2. Mit dem Rückgriff auf die Adresse war es auf `/projekte/<id>/…` gelöst,
 *    aber nicht auf „Heute": dort steht keine Projektkennung im Pfad, also
 *    blieb die Gruppe weiter leer.
 *
 * Deshalb drei Stufen, in dieser Reihenfolge:
 *
 *   1. die ausdrückliche Auswahl, wenn sie auf ein sichtbares Projekt zeigt
 *   2. das Projekt aus der Adresse — dort ist der Kontext eindeutig, auch ohne
 *      Auswahl
 *   3. das erste sichtbare Projekt, damit die Gruppe überhaupt steht
 *
 * Stufe 3 ist eine Annahme, aber eine sichtbare: Die Gruppe trägt den Namen des
 * Projekts in ihrer Überschrift („Projekt CUB"). Niemand sieht Meilensteine
 * eines Projekts, ohne zu lesen, welches gemeint ist. Die Alternative wäre eine
 * Anwendung, die ihre halbe Navigation verbirgt, bis man den Kontextschalter
 * gefunden hat.
 *
 * „Alle Projekte" bleibt trotzdem, was es ist — der Arbeitsraum für Aufgaben
 * und Klärungen. Diese Funktion beantwortet nur, welches Projekt die
 * **Projektgruppe** zeigt.
 */

export type Projektkontext = {
  id: string;
  kuerzel: string | null;
  name: string;
};

/** Die Projektkennung aus `/projekte/<id>/…`, falls die Adresse eine trägt. */
export function projektIdAusPfad(pfad: string): string | undefined {
  return /^\/projekte\/([^/]+)/.exec(pfad)?.[1];
}

export function waehleProjektkontext<T extends Projektkontext>({
  auswahl,
  alleWert,
  projekte,
  pfad,
}: {
  auswahl: string;
  alleWert: string;
  projekte: readonly T[];
  pfad: string;
}): T | undefined {
  if (projekte.length === 0) return undefined;

  if (auswahl !== alleWert) {
    const gewaehlt = projekte.find((p) => p.id === auswahl);
    if (gewaehlt) return gewaehlt;
  }

  const ausPfad = projektIdAusPfad(pfad);
  if (ausPfad) {
    const getroffen = projekte.find((p) => p.id === ausPfad);
    if (getroffen) return getroffen;
  }

  return projekte[0];
}

/**
 * Wohin ein Projektwechsel führt.
 *
 * Wer den Puls eines Projekts liest und auf ein anderes wechselt, will dessen
 * Puls sehen — nicht auf „Heute" landen und den Weg zurück suchen. Die Seite
 * ist die Absicht, das Projekt nur ihr Gegenstand.
 *
 * Drei Fälle, in dieser Reihenfolge:
 *
 *   1. Der Pfad trägt eine Projektkennung: Sie wird ausgetauscht, alles andere
 *      bleibt stehen — auch der Reiter, denn der steckt in der Abfrage.
 *   2. Der Pfad zeigt auf ein einzelnes Objekt des alten Projekts (eine Seite,
 *      einen Meilenstein, ein Arbeitspaket, eine Aufgabe): Das Gegenstück im
 *      neuen Projekt gibt es nicht, also führt der Weg eine Ebene höher.
 *   3. Der Pfad ist projektübergreifend („Heute", Einstellungen, Metarr):
 *      `undefined` — hier ist nichts zu tun, die Seite bleibt einfach stehen.
 */
export function zielNachProjektwechsel(
  pfad: string,
  neueProjektId: string,
): string | undefined {
  // Eine einzelne Aufgabe gehört dem alten Projekt; die Liste trägt beide.
  if (/^\/aufgaben\/[^/]+$/.test(pfad)) return "/aufgaben";

  const treffer = /^\/projekte\/([^/]+)(\/.*)?$/.exec(pfad);
  if (!treffer) return undefined;

  const rest = treffer[2] ?? "";
  const ziel = `/projekte/${neueProjektId}`;

  // Objektpfade tragen eine fremde Kennung — zurück auf ihre Liste.
  const objekt = /^\/(seiten|meilensteine|arbeitspakete)\/[^/]+$/.exec(rest);
  if (objekt) {
    // Seiten haben eine eigene Liste; Meilensteine und Arbeitspakete wohnen
    // im Reiter der Projektübersicht.
    return objekt[1] === "seiten"
      ? `${ziel}/seiten`
      : `${ziel}?reiter=meilensteine`;
  }

  return `${ziel}${rest}`;
}
